import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Col, Container, Nav, Navbar, Row } from 'react-bootstrap';
import { CurrencyContext } from '../../utils/currencyContext';
import getCurrencySign from '../../utils/getCurrencySign';
import TopBanner from '../../components/topBanner';
import HostingWidget from '../../components/hostingWidget';
import FeaturesWidget from '../../components/featuresWidget';
import CpanelWidget from '../../components/cpanelWidget';
import FaqWidget from '../../components/faqWidget';
import ResellerPlansWidget from '../../components/resellerPlansWidget';
import ContactBanner from '../../components/contactBanner/ContactBanner';
import { resellerPlanPrices } from '../../resources/static';
import webBannerEn from '../../assets/img/reseller-main.svg';
import releaseIcon from '../../assets/img/release-icon-black.svg';
import startIcon from '../../assets/img/star-orange.svg';
import infoBanner1 from '../../assets/img/info-banner-1.svg';
import infoBanner2 from '../../assets/img/info-banner-2.svg';
import infoBanner3 from '../../assets/img/info-banner-3.svg';
import './hostingReseller.scss';

function HostingResellerPage() {
    const { i18n, t } = useTranslation();
    const { currentCurrency } = useContext(CurrencyContext);
    const resellerData = resellerPlanPrices[currentCurrency];
    const sign = getCurrencySign(currentCurrency);
    const currentLanguage = i18n.language;
    const isFR = currentLanguage === 'fr';

    const getCurrencyCode = (currency) => {
        let currencyCode = 1;

        if (currency === 'usd') {
            currencyCode = 4;
        } else if (currency === 'gbp') {
            currencyCode = 5;
        }

        return currencyCode;
    };

    return (
        <>
            <Helmet>
                <title>{t('titles.hosting-reseller')}</title>
            </Helmet>
            <div className="reseller-page-wrapper">
                <TopBanner
                    bannerImage={<img src={webBannerEn} alt="Reseller hosting banner" />}
                    className="reseller-top-banner"
                >
                    <>
                        <h1 className="banner-header">{t('reseller-hosting-page.main-title')}</h1>
                        <h2 className="banner-sub-header">
                            {t('reseller-hosting-page.main-text')}
                        </h2>
                        <div className="banner-card">
                            <div className="reseller-header-wrapper">
                                <h3 className="reseller-header">
                                    Reseller <span>One</span>
                                </h3>
                                <div className="banner-badges">
                                    <span className="single-badge">
                                        <img src={startIcon} alt="Star" />
                                        {t('titles.unique-plan')}
                                    </span>
                                </div>
                            </div>
                            <p className="reseller-text">
                                {t('reseller-hosting-page.reseller-banner-text')}
                            </p>
                            <ul className="banner-list">
                                <li className="banner-item">
                                    <img src={releaseIcon} alt="List item" />
                                    <p className="banner-text">
                                        {t('reseller-hosting-page.reseller-banner-list-1')}
                                    </p>
                                </li>
                                <li className="banner-item">
                                    <img src={releaseIcon} alt="List item" />
                                    <p className="banner-text">
                                        {t('reseller-hosting-page.reseller-banner-list-2')}
                                    </p>
                                </li>
                                <li className="banner-item">
                                    <img src={releaseIcon} alt="List item" />
                                    <p className="banner-text">
                                        {t('reseller-hosting-page.reseller-banner-list-3')}
                                    </p>
                                </li>
                            </ul>
                            <div className="reseller-info">
                                <div className="info-column">
                                    <p className="row-first">
                                        <strong>10</strong>{' '}
                                        {t('reseller-hosting-page.reseller-text-small-1')}
                                    </p>
                                    <p className="row-second">
                                        ({t('titles.then')}{' '}
                                        <span>
                                            {resellerData.extra_account}
                                            {sign}
                                        </span>{' '}
                                        {t('titles.per')} {t('titles.account')})
                                    </p>
                                </div>
                                <div className="info-square" />
                                <div className="info-column">
                                    <p className="row-first">
                                        <strong>100{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                        {t('reseller-hosting-page.reseller-text-small-2')}
                                    </p>
                                    <p className="row-second">
                                        ({t('titles.then')}{' '}
                                        <span>
                                            {resellerData.extra_gb}
                                            {sign}
                                        </span>{' '}
                                        {t('titles.per')} {t('web-hosting-page.plans-column.gb')})
                                    </p>
                                </div>
                            </div>
                            <hr className="banner-separator" />
                            <div className="banner-actions">
                                <div className="banner-price">
                                    <span className="new-price">
                                        {resellerData.per_month}
                                        <sup>{sign}</sup>
                                    </span>
                                    <span className="price-currency">/ {t('titles.month')}</span>
                                </div>
                                <Nav>
                                    <Nav.Link
                                        href={`https://obambu.com/order/?pid=148&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}`}
                                        rel="noopener noreferrer"
                                    >
                                        <Navbar.Brand>{t('buttons.order-now')}</Navbar.Brand>
                                    </Nav.Link>
                                </Nav>
                            </div>
                        </div>
                    </>
                </TopBanner>
                <section className="info-banner">
                    <Container>
                        <Row>
                            <Col lg={4}>
                                <div className="info-banner-block">
                                    <img
                                        className="info-image"
                                        src={infoBanner1}
                                        alt="Info banner"
                                    />
                                    <h3 className="info-banner-title">
                                        {t('banners.info-banner-web.col-1-text')}
                                    </h3>
                                </div>
                            </Col>
                            <Col lg={4}>
                                <div className="info-banner-block">
                                    <img
                                        className="info-image"
                                        src={infoBanner2}
                                        alt="Info banner"
                                    />
                                    <h3 className="info-banner-title">
                                        {t('banners.info-banner-web.col-2-text')}
                                    </h3>
                                </div>
                            </Col>
                            <Col lg={4}>
                                <div className="info-banner-block">
                                    <img
                                        className="info-image"
                                        src={infoBanner3}
                                        alt="Info banner"
                                    />
                                    <h3 className="info-banner-title">
                                        {t('banners.info-banner-web.col-3-text')}
                                    </h3>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <section className="section-wrapper hosting-section">
                    <Container>
                        <div className="section-top">
                            <h2
                                className="section-title"
                                dangerouslySetInnerHTML={{
                                    __html: t('reseller-hosting-page.hosting-title')
                                }}
                            />
                            <p className="section-text">
                                {t('reseller-hosting-page.hosting-text')}
                            </p>
                        </div>
                        <HostingWidget dataType="reseller" />
                    </Container>
                </section>
                <section className="section-wrapper features-section">
                    <Container>
                        <div className="section-top">
                            <h2
                                className="section-title"
                                dangerouslySetInnerHTML={{
                                    __html: t('reseller-hosting-page.features-title')
                                }}
                            />
                            <p className="section-text">
                                {t('reseller-hosting-page.features-text')}
                            </p>
                        </div>
                        <FeaturesWidget dataType="reseller" />
                    </Container>
                </section>
                <section className="section-wrapper cpanel-section">
                    <Container>
                        <div className="section-top">
                            <h2
                                className="section-title"
                                dangerouslySetInnerHTML={{
                                    __html: t('reseller-hosting-page.cpanel-title')
                                }}
                            />
                            <p className="section-text">{t('reseller-hosting-page.cpanel-text')}</p>
                        </div>
                        <CpanelWidget dataType="reseller" />
                    </Container>
                </section>
                <section className="regular-banner p-5">
                    <h3 className="banner-title">{t('banners.regular-banner-reseller-text')}</h3>
                </section>
                <section className="section-wrapper extended-plans-section" id="all-plans-section">
                    <Container>
                        <div className="section-top">
                            <h2
                                className="section-title"
                                dangerouslySetInnerHTML={{
                                    __html: t('reseller-hosting-page.reseller-plans-title')
                                }}
                            />
                            <p className="section-text">
                                {t('reseller-hosting-page.reseller-plans-text')}
                            </p>
                        </div>
                        <ResellerPlansWidget />
                    </Container>
                </section>
                <section className="section-wrapper faq-section">
                    <Container>
                        <div className="section-top">
                            <h2
                                className="section-title"
                                dangerouslySetInnerHTML={{
                                    __html: t('reseller-hosting-page.faq-title')
                                }}
                            />
                            <p className="section-text">{t('reseller-hosting-page.faq-text')}</p>
                        </div>
                        <FaqWidget dataType="reseller" />
                    </Container>
                </section>
                <ContactBanner />
            </div>
        </>
    );
}

export default HostingResellerPage;
